import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { MyceliumText, PulsingNode, CountUp, StaggerContainer, StaggerItem, RevealWrapper, SectionConnector, organicEase, FloatingWrapper } from '../components/MyceliumAnimations';
export function HomeThreeTruths() {
  return <div className="min-h-screen bg-black" data-id="element-1105" data-mp="1g531sbwqrqpy">
      {/* Hero - Triptych Preview */}
      <section aria-label="Vitality Venture Fund — Investing in nature-inspired innovation" className="relative h-screen flex items-center justify-center overflow-hidden" data-id="element-1106" data-mp="1l3mnqcbc52ib">
        {/* Press Banner - Consolidated at top */}
        <motion.div initial={{
        opacity: 0,
        y: -20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.5,
        duration: 0.6
      }} className="absolute top-24 left-0 right-0 z-20 px-4" data-id="element-1107" data-mp="1u0x1c4bml3qe">
          <div className="max-w-7xl mx-auto" data-id="element-1108" data-mp="19gjndweksnhv">
            <div className="bg-black/40 backdrop-blur-md border border-vitalityTeal/20 rounded-full px-4 sm:px-6 py-3 inline-flex items-center gap-2 sm:gap-4 mx-auto flex-wrap justify-center" data-id="element-1109" data-mp="19huqrv43zt8g">
              <span className="text-gray-400 text-xs uppercase tracking-wider" data-id="element-1110" data-mp="zinhpk4eqkr5">
                As featured in
              </span>
              <div className="flex items-center gap-2 sm:gap-4 flex-wrap justify-center" data-id="element-1111" data-mp="qqll5wxglom5">
                <a href="https://www.ibtimes.com/vitality-vision-natalya-wallins-full-circle-journey-investing-nature-inspired-innovation-3779469" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-vitalityTeal transition-colors text-xs font-medium py-1" data-id="element-1112" data-mp="qoqvk5b8t8bq">
                  International Business Times
                </a>
                <span className="text-gray-600 hidden sm:inline" data-id="element-1113" data-mp="3tbqjnvw0874o">•</span>
                <a href="https://ceoweekly.com/the-next-big-wave-in-tech-is-ancient-how-natalya-wallin-and-vitality-are-betting-on-nature-to-solve-modern-problems/" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-vitalityTeal transition-colors text-xs font-medium py-1" data-id="element-1114" data-mp="25gtrpwcvy57">
                  CEO Weekly
                </a>
                <span className="text-gray-600 hidden sm:inline" data-id="element-1115" data-mp="kuwvggjs1wn7">•</span>
                <a href="https://www.usatoday.com/story/special/contributor-content/2025/11/03/its-time-to-rethink-what-and-who-we-fund-says-vitality-founder-natalya-wallin/87064743007/" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-vitalityTeal transition-colors text-xs font-medium py-1" data-id="element-1116" data-mp="75bm7xvs4vsd">
                  USA Today
                </a>
                <span className="text-gray-600 hidden sm:inline" data-id="element-1117" data-mp="1dwwl4frjh0eh">•</span>
                <a href="https://www.pivotol.com/at-depth-podcast-episodes/natalya-wallin" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-vitalityTeal transition-colors text-xs font-medium py-1" data-id="element-1118" data-mp="19ww8n0o02za2">
                  At Depth Podcast
                </a>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Three images side by side */}
        <div className="absolute inset-0 grid grid-cols-3" data-id="element-1119" data-mp="14zcgqxydqnzn">
          {[{
          src: 'https://cdn.magicpatterns.com/uploads/68H7NDRoLcn1FKiHRZijKp/10_SunsettingCanyon.jpg',
          alt: 'Sunset over ancient canyon landscape representing endurance and long-term thinking'
        }, {
          src: 'https://cdn.magicpatterns.com/uploads/c6jzDkT8Z7Z2KhqiesKc4w/11_MoreViews.jpg',
          alt: 'Expansive natural vista symbolizing regenerative systems and growth'
        }, {
          src: 'https://cdn.magicpatterns.com/uploads/qvkzqkkci4tMqBQJeNxkje/3_NatSnow.jpg',
          alt: 'Natalya Wallin in winter wilderness demonstrating conviction and resilience'
        }].map((img, i) => <motion.div key={i} className="relative overflow-hidden" initial={{
          opacity: 0,
          scale: 1.1
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          delay: i * 0.2,
          duration: 1.2,
          ease: organicEase
        }} data-id="element-1120" data-mp="13wx8z4edytim" data-mpi={i}>
              <img src={img.src} alt={img.alt} className="w-full h-full object-cover" data-id="element-1121" data-mp="1mkk5ylar8hv9" />
              <motion.div className="absolute inset-0 bg-black/60" initial={{
            opacity: 0.8
          }} animate={{
            opacity: 0.6
          }} transition={{
            delay: 0.5 + i * 0.2,
            duration: 1
          }} data-id="element-1122" data-mp="3c40p8ws8oxgi" />
            </motion.div>)}
        </div>

        {/* Floating decorative nodes */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden" data-id="element-1123" data-mp="3qsn3swa1ll6o">
          <FloatingWrapper className="absolute top-1/4 left-[15%]" amplitude={12} duration={5} data-id="element-1124" data-mp="3quri885ki2l7">
            <PulsingNode size={6} delay={0.5} intensity="low" data-id="element-1125" data-mp="3h3n6dhb6uxul" />
          </FloatingWrapper>
          <FloatingWrapper className="absolute top-1/3 left-[50%]" amplitude={15} duration={4.5} data-id="element-1126" data-mp="idd9wbgb3m9z">
            <PulsingNode size={8} delay={1} intensity="medium" data-id="element-1127" data-mp="dktura1z0j32" />
          </FloatingWrapper>
          <FloatingWrapper className="absolute top-1/4 right-[15%]" amplitude={10} duration={5.5} data-id="element-1128" data-mp="wix0xe0w2b8o">
            <PulsingNode size={5} delay={1.5} intensity="low" data-id="element-1129" data-mp="nqbkw5on6inj" />
          </FloatingWrapper>
        </div>

        <div className="relative z-10 text-center px-4 max-w-5xl mx-auto" data-id="element-1130" data-mp="rrey0kfb20l2">
          <motion.h1 className="text-6xl md:text-8xl font-bold text-white mb-8" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.3,
          duration: 0.8
        }} data-id="element-1131" data-mp="rpap1o2jubdx">
            <MyceliumText as="span" delay={0.3} staggerChildren={0.04} data-id="element-1132" data-mp="8s9hbvhm1x6a">
              Investing in a world of Vitality
            </MyceliumText>
          </motion.h1>

          <RevealWrapper delay={0.4} data-id="element-1133" data-mp="1z3e3t7e7rkav">
            <p className="text-xl md:text-2xl text-vitalityTeal" data-id="element-1134" data-mp="2373q1q7f1psq">
              Fueling startups built on nature's intelligence
            </p>
          </RevealWrapper>
        </div>
      </section>

      <SectionConnector data-id="element-1135" data-mp="3jo2e4xr9q2eu" />

      {/* Section Title for Three Truths */}
      <section className="py-16 bg-black" data-id="element-1136" data-mp="199yps1z49nz">
        <div className="max-w-4xl mx-auto px-4 text-center" data-id="element-1137" data-mp="1a1v353eabfb">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-4xl md:text-5xl font-bold text-white mb-4" data-id="element-1138" data-mp="3so7kd4kq6urf">
            <MyceliumText as="span" staggerChildren={0.04} data-id="element-1139" data-mp="k6ukurdcsu4w">
              Three truths shape how we invest to drive results that matter
            </MyceliumText>
          </motion.h2>
        </div>
      </section>

      <SectionConnector data-id="element-1140" data-mp="2lzr95fw9xt6y" />

      {/* Truth 1 - Nature Endures */}
      <article aria-label="First Truth: What Endures, Matters" className="min-h-screen flex items-center relative overflow-hidden" data-id="element-1141" data-mp="2h17hcyv01n6o">
        <div className="absolute inset-0" data-id="element-1142" data-mp="1w2xa62qoe5cr">
          <motion.img src="https://cdn.magicpatterns.com/uploads/68H7NDRoLcn1FKiHRZijKp/10_SunsettingCanyon.jpg" alt="Ancient canyon at sunset — billions of years of geological endurance" className="w-full h-full object-cover" initial={{
          scale: 1.1
        }} whileInView={{
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 1.5,
          ease: organicEase
        }} data-id="element-1143" data-mp="205a4luzz2gu8" />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-transparent" data-id="element-1144" data-mp="1r5dowivk8sbb" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-id="element-1145" data-mp="2xminl36r03gl">
          <div className="max-w-2xl" data-id="element-1146" data-mp="32kk8ltnqicup">
            <RevealWrapper direction="left" data-id="element-1147" data-mp="32jiwrmyw9mbx">
              <div className="flex items-center gap-4 mb-6" data-id="element-1148" data-mp="qxgwl0ps2gbv">
                <motion.div className="w-12 h-12 rounded-full bg-vitalityTeal/20 border-2 border-vitalityTeal flex items-center justify-center" animate={{
                boxShadow: ['0 0 0px rgba(43, 199, 183, 0.3)', '0 0 20px rgba(43, 199, 183, 0.5)', '0 0 0px rgba(43, 199, 183, 0.3)']
              }} transition={{
                duration: 3,
                repeat: Infinity
              }} data-id="element-1149" data-mp="qwxcbdcnnfuo">
                  <span className="text-vitalityTeal font-bold text-xl" data-id="element-1150" data-mp="3ueyt4c03o0wz">
                    01
                  </span>
                </motion.div>
                <p className="text-vitalityTeal text-sm font-bold tracking-wider uppercase" data-id="element-1151" data-mp="1smu4paht7lr0">
                  First Truth
                </p>
              </div>
            </RevealWrapper>

            <motion.h2 className="text-5xl md:text-7xl font-bold text-white mb-8 leading-tight" initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} viewport={{
            once: true
          }} data-id="element-1152" data-mp="1nlwqta3u1dvb">
              <MyceliumText as="span" staggerChildren={0.05} data-id="element-1153" data-mp="1hdww9174sanu">
                What Endures, Matters
              </MyceliumText>
            </motion.h2>

            <StaggerContainer className="space-y-6 text-gray-300 text-lg leading-relaxed" staggerDelay={0.15} data-id="element-1154" data-mp="tmhkyixdmj0g">
              <StaggerItem data-id="element-1155" data-mp="13fgod6xj8e44">
                <p data-id="element-1156" data-mp="13fq481ov54w3">
                  Nature has survived billions of years of shocks, cycles, and
                  collapse. Most modern systems haven't survived a single
                  century without fragility.
                </p>
              </StaggerItem>
              <StaggerItem data-id="element-1157" data-mp="5zsz5m58y6ek">
                <p data-id="element-1158" data-mp="5yhwjpnjf4p1">
                  Vitality invests in companies designed for resilience and
                  endurance, businesses built to last and thrive through
                  volatility. We favor long time horizons, durable advantages,
                  and founders willing to trade speed for staying power.
                </p>
              </StaggerItem>
              <StaggerItem data-id="element-1159" data-mp="2cmi87bgglj68">
                <p className="text-white font-semibold" data-id="element-1160" data-mp="2cnthtrwl5l59">
                  Because what endures is what ultimately matters.
                </p>
              </StaggerItem>
            </StaggerContainer>

            <RevealWrapper delay={0.6} data-id="element-1161" data-mp="35b62fwj6rivx">
              <div className="mt-12 pt-8 border-t border-white/10" data-id="element-1162" data-mp="2rcg3scgpxb5g">
                <p className="text-white font-semibold mb-3" data-id="element-1163" data-mp="2re9294h6gp2d">
                  Long-Term Thinking
                </p>
                <p className="text-gray-400" data-id="element-1164" data-mp="1fx0waeqvv0w3">
                  3 billion years of evolution. Millions of years of geological
                  time. We measure success in decades, not quarters.
                </p>
              </div>
            </RevealWrapper>
          </div>
        </div>
      </article>

      <SectionConnector data-id="element-1165" data-mp="3hm6vrhgcep25" />

      {/* Truth 2 - Nature Regenerates */}
      <article aria-label="Second Truth: What Regenerates, Compounds" className="min-h-screen flex items-center relative overflow-hidden bg-gradient-to-b from-black to-[#0a1f2e]" data-id="element-1166" data-mp="3ho9sekc03gfu">
        <div className="absolute inset-0" data-id="element-1167" data-mp="37uas7qvhm6qj">
          <motion.img src="https://cdn.magicpatterns.com/uploads/c6jzDkT8Z7Z2KhqiesKc4w/11_MoreViews.jpg" alt="Forest landscape demonstrating nature's regenerative capacity" className="w-full h-full object-cover opacity-50" initial={{
          scale: 1.1
        }} whileInView={{
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 1.5,
          ease: organicEase
        }} data-id="element-1168" data-mp="33v20erk0cixm" />
          <div className="absolute inset-0 bg-gradient-to-l from-[#0a1f2e] via-[#0a1f2e]/90 to-transparent" data-id="element-1169" data-mp="vn57xiofexa7" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-id="element-1170" data-mp="zo8knwb6gd77">
          <div className="max-w-2xl ml-auto" data-id="element-1171" data-mp="14fqoqt8hytwe">
            <RevealWrapper direction="right" data-id="element-1172" data-mp="14g0gal96x947">
              <div className="flex items-center gap-4 mb-6" data-id="element-1173" data-mp="19ejzwf0ba8te">
                <motion.div className="w-12 h-12 rounded-full bg-vitalityTeal/20 border-2 border-vitalityTeal flex items-center justify-center" animate={{
                boxShadow: ['0 0 0px rgba(43, 199, 183, 0.3)', '0 0 20px rgba(43, 199, 183, 0.5)', '0 0 0px rgba(43, 199, 183, 0.3)']
              }} transition={{
                duration: 3,
                repeat: Infinity,
                delay: 0.5
              }} data-id="element-1174" data-mp="19ds5v8md4j58">
                  <span className="text-vitalityTeal font-bold text-xl" data-id="element-1175" data-mp="225bsl2i64fbz">
                    02
                  </span>
                </motion.div>
                <p className="text-vitalityTeal text-sm font-bold tracking-wider uppercase" data-id="element-1176" data-mp="361umgysryr5f">
                  Second Truth
                </p>
              </div>
            </RevealWrapper>

            <motion.h2 className="text-5xl md:text-7xl font-bold text-white mb-8 leading-tight" initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} viewport={{
            once: true
          }} data-id="element-1177" data-mp="3f1hsjxwg8l4t">
              <MyceliumText as="span" staggerChildren={0.05} data-id="element-1178" data-mp="6l66twxh9l3p">
                What Regenerates, Compounds
              </MyceliumText>
            </motion.h2>

            <StaggerContainer className="space-y-6 text-gray-300 text-lg leading-relaxed" staggerDelay={0.15} data-id="element-1179" data-mp="gjc7kb7jsl6z">
              <StaggerItem data-id="element-1180" data-mp="bixz4s21b98f">
                <p data-id="element-1181" data-mp="v73vmebngoq4">
                  In nature, disruption doesn't end systems, it reshapes them.
                  Living systems regenerate, adapt, and grow stronger over time.
                </p>
              </StaggerItem>
              <StaggerItem data-id="element-1182" data-mp="l7mri1c1qiw9">
                <p data-id="element-1183" data-mp="l8enhd9k2in2">
                  We invest in founders who apply this logic to the modern
                  world. Companies that work with natural systems rather than
                  against them, unlocking compounding benefits across
                  efficiency, resilience, and cost by combining biomimicry with
                  modern technology.
                </p>
              </StaggerItem>
            </StaggerContainer>

            <RevealWrapper delay={0.6} data-id="element-1184" data-mp="7h02x20029dw">
              <div className="mt-12 pt-8 border-t border-white/10" data-id="element-1185" data-mp="7i1h3fcbbbzu">
                <p className="text-white font-semibold mb-4" data-id="element-1186" data-mp="3pmctbx02g4do">
                  Three Investment Themes
                </p>
                <StaggerContainer className="grid grid-cols-3 gap-4" staggerDelay={0.1} data-id="element-1187" data-mp="3tnew2nt6yyke">
                  {[{
                  label: 'Grow',
                  desc: 'Agriculture & Food'
                }, {
                  label: 'Build',
                  desc: 'Construction'
                }, {
                  label: 'Use',
                  desc: 'Materials'
                }].map((item, i) => <StaggerItem key={item.label} data-id="element-1188" data-mp="2e58u6ngfi79z" data-mpi={i}>
                      <motion.div className="text-center" whileHover={{
                    scale: 1.05,
                    textShadow: '0 0 10px rgba(43, 199, 183, 0.5)'
                  }} data-id="element-1189" data-mp="2e3fobadn3r31">
                        <p className="text-vitalityTeal font-bold mb-1" data-id="element-1190" data-mp="xslffk6zhde5">
                          {item.label}
                        </p>
                        <p className="text-gray-500 text-sm" data-id="element-1191" data-mp="35dae28k1j2d3">{item.desc}</p>
                      </motion.div>
                    </StaggerItem>)}
                </StaggerContainer>
              </div>
            </RevealWrapper>
          </div>
        </div>
      </article>

      <SectionConnector data-id="element-1192" data-mp="3j4f6oqozqudx" />

      {/* Truth 3 - Nature Requires Conviction */}
      <article aria-label="Third Truth: What Matters, Demands Conviction" className="min-h-screen flex items-center relative overflow-hidden bg-[#0a1f2e]" data-id="element-1193" data-mp="3j6i43vsh3pso">
        <div className="absolute inset-0" data-id="element-1194" data-mp="3t11qdc5kzfed">
          <motion.img src="https://cdn.magicpatterns.com/uploads/qvkzqkkci4tMqBQJeNxkje/3_NatSnow.jpg" alt="Natalya Wallin enduring winter conditions — conviction through adversity" className="w-full h-full object-cover opacity-60" initial={{
          scale: 1.1
        }} whileInView={{
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 1.5,
          ease: organicEase
        }} data-id="element-1195" data-mp="ysmry4o8a3p" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a1f2e] via-[#0a1f2e]/90 to-[#0a1f2e]/70" data-id="element-1196" data-mp="9dq9hnvuwa7g" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-id="element-1197" data-mp="i8einp4jyc1j">
          <div className="max-w-2xl" data-id="element-1198" data-mp="3qny627ijl08o">
            <RevealWrapper direction="left" data-id="element-1199" data-mp="3qozhwe7dtqrg">
              <div className="flex items-center gap-4 mb-6" data-id="element-1200" data-mp="3qq0x8uvfkzez">
                <motion.div className="w-12 h-12 rounded-full bg-vitalityTeal/20 border-2 border-vitalityTeal flex items-center justify-center" animate={{
                boxShadow: ['0 0 0px rgba(43, 199, 183, 0.3)', '0 0 20px rgba(43, 199, 183, 0.5)', '0 0 0px rgba(43, 199, 183, 0.3)']
              }} transition={{
                duration: 3,
                repeat: Infinity,
                delay: 1
              }} data-id="element-1201" data-mp="3mno18y6hkfve">
                  <span className="text-vitalityTeal font-bold text-xl" data-id="element-1202" data-mp="2xv2zyt2kk954">
                    03
                  </span>
                </motion.div>
                <p className="text-vitalityTeal text-sm font-bold tracking-wider uppercase" data-id="element-1203" data-mp="2osjzgfm9a8m1">
                  Third Truth
                </p>
              </div>
            </RevealWrapper>

            <motion.h2 className="text-5xl md:text-7xl font-bold text-white mb-8 leading-tight" initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} viewport={{
            once: true
          }} data-id="element-1204" data-mp="3gjzumy1vp3nv">
              <MyceliumText as="span" staggerChildren={0.04} data-id="element-1205" data-mp="3vg8rxu27l6xn">
                What Matters, Demands Conviction
              </MyceliumText>
            </motion.h2>

            <StaggerContainer className="space-y-6 text-gray-300 text-lg leading-relaxed" staggerDelay={0.15} data-id="element-1206" data-mp="kcddp9r0iwk1">
              <StaggerItem data-id="element-1207" data-mp="paop3owyhf4d">
                <p data-id="element-1208" data-mp="pd117lg18v9i">
                  The most important problems are rarely the easiest to solve.
                </p>
              </StaggerItem>
              <StaggerItem data-id="element-1209" data-mp="fkbsuzfs3xie">
                <p data-id="element-1210" data-mp="fjk9cmzghq8h">
                  Building nature-inspired companies requires patience,
                  technical depth, and the willingness to persist through
                  uncertainty. These aren't linear journeys, and they aren't for
                  founders or investors optimizing for quick wins.
                </p>
              </StaggerItem>
              <StaggerItem data-id="element-1211" data-mp="3sr57gmuytx3j">
                <p data-id="element-1212" data-mp="3ssf5jc1tj5a6">
                  Vitality backs founders with the conviction to build through
                  complexity and adapt. Our team knows that meaningful change
                  takes time.
                </p>
              </StaggerItem>
            </StaggerContainer>

            <RevealWrapper delay={0.6} data-id="element-1213" data-mp="3bje0smde29fl">
              <div className="mt-12" data-id="element-1214" data-mp="3bg8a52p42zhr">
                <Link to="/founders" data-id="element-1215" data-mp="37hsg4eum4quu">
                  <motion.div whileHover={{
                  scale: 1.02,
                  boxShadow: '0 0 30px rgba(43, 199, 183, 0.4)'
                }} transition={{
                  duration: 0.3
                }} className="inline-block rounded-lg" data-id="element-1216" data-mp="37iu4fl0n39ui">
                    <Button variant="primary" data-id="element-1217" data-mp="3q8a4y8cbr2ax">
                      Meet the Team
                      <ArrowRight className="w-4 h-4 ml-2" data-id="element-1218" data-mp="82dy6129aqz8" />
                    </Button>
                  </motion.div>
                </Link>
              </div>
            </RevealWrapper>
          </div>
        </div>
      </article>

      <SectionConnector data-id="element-1219" data-mp="29u0gle831dk3" />

      {/* Synthesis - How These Truths Inform Our Thesis */}
      <section className="py-32 bg-gradient-to-b from-[#0a1f2e] to-black" data-id="element-1220" data-mp="24v7f6k62ld0a">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1221" data-mp="8r9g81c6g372">
          <motion.div initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} className="text-center" data-id="element-1222" data-mp="8t42biz6q1cq">
            <motion.h2 className="text-4xl md:text-5xl font-bold text-white mb-8" initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} viewport={{
            once: true
          }} data-id="element-1223" data-mp="mrk4cwm3r3oj">
              <MyceliumText as="span" staggerChildren={0.04} data-id="element-1224" data-mp="11hrpxl7ep2tg">
                We're Investing in a World of Vitality
              </MyceliumText>
            </motion.h2>

            <RevealWrapper delay={0.2} data-id="element-1225" data-mp="1aho7l2vdp1f0">
              <p className="text-xl text-gray-300 leading-relaxed mb-6 max-w-3xl mx-auto" data-id="element-1226" data-mp="1aeik1s2ee7le">
                The world doesn't need more incremental solutions. It needs a
                different way of thinking.
              </p>
              <p className="text-xl text-gray-300 leading-relaxed mb-16 max-w-3xl mx-auto" data-id="element-1227" data-mp="2uxw2ogza78em">
                Vitality exists to fund that shift—and we're backing founders
                who follow these three truths.
              </p>
            </RevealWrapper>

            <StaggerContainer className="grid md:grid-cols-3 gap-12 mb-16" staggerDelay={0.15} data-id="element-1228" data-mp="2m0293dswisv5">
              {[{
              title: 'Endurance',
              desc: 'Investing in the companies built to last',
              icon: '1'
            }, {
              title: 'Regeneration',
              desc: 'Compounding value by mimicking living systems',
              icon: '2'
            }, {
              title: 'Conviction',
              desc: 'Backing founders who build through complexity',
              icon: '3'
            }].map((principle, index) => <StaggerItem key={principle.title} data-id="element-1229" data-mp="3m5chgoxw7gp5" data-mpi={index}>
                  <motion.div className="text-center p-6 rounded-xl" whileHover={{
                backgroundColor: 'rgba(43, 199, 183, 0.05)',
                boxShadow: '0 0 30px rgba(43, 199, 183, 0.15)'
              }} transition={{
                duration: 0.3
              }} data-id="element-1230" data-mp="3m64e83387kh0">
                    <motion.div className="w-16 h-16 rounded-full bg-vitalityTeal/10 border-2 border-vitalityTeal flex items-center justify-center mx-auto mb-4" animate={{
                  boxShadow: ['0 0 0px rgba(43, 199, 183, 0.2)', '0 0 15px rgba(43, 199, 183, 0.4)', '0 0 0px rgba(43, 199, 183, 0.2)']
                }} transition={{
                  duration: 2.5,
                  repeat: Infinity,
                  delay: index * 0.3
                }} data-id="element-1231" data-mp="8p513fc461l5">
                      <span className="text-vitalityTeal font-bold text-xl" data-id="element-1232" data-mp="1bhr0wt8tp3nq">
                        {principle.icon}
                      </span>
                    </motion.div>
                    <h3 className="text-xl font-bold text-white mb-3" data-id="element-1233" data-mp="iumucju30h9u">
                      {principle.title}
                    </h3>
                    <p className="text-gray-400" data-id="element-1234" data-mp="eumf4wz5s21q">{principle.desc}</p>
                  </motion.div>
                </StaggerItem>)}
            </StaggerContainer>

            <RevealWrapper delay={0.5} data-id="element-1235" data-mp="nrdaf75vzcv9">
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-1236" data-mp="2477u3t49ool0">
                <Link to="/approach" data-id="element-1237" data-mp="1z83kj1dfkou5">
                  <motion.div whileHover={{
                  scale: 1.02,
                  boxShadow: '0 0 30px rgba(43, 199, 183, 0.4)'
                }} transition={{
                  duration: 0.3
                }} className="rounded-lg" data-id="element-1238" data-mp="1z71w7v7em5uh">
                    <Button variant="primary" data-id="element-1239" data-mp="2g7o8kgdbmeq6">
                      Our Approach
                      <ArrowRight className="w-4 h-4 ml-2" data-id="element-1240" data-mp="2l7soo5abh7fv" />
                    </Button>
                  </motion.div>
                </Link>
                <Link to="/portfolio" data-id="element-1241" data-mp="1sjn2de3m7adx">
                  <motion.div whileHover={{
                  scale: 1.02,
                  boxShadow: '0 0 20px rgba(43, 199, 183, 0.2)'
                }} transition={{
                  duration: 0.3
                }} className="rounded-lg" data-id="element-1242" data-mp="1skg39yfw75o5">
                    <Button variant="outline" data-id="element-1243" data-mp="19t5f3p1jx6zy">View Portfolio</Button>
                  </motion.div>
                </Link>
              </div>
            </RevealWrapper>
          </motion.div>
        </div>
      </section>
    </div>;
}